import { TextField } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSocket } from '@/context/SocketContext';
import useChats from '@/hooks/useChats';
import useReadChat from '@/hooks/useReadChat';
import React, { FC, useCallback, useEffect, useRef } from 'react';
import { Chat, Message, type OnSocketNewMessage } from '@/types';
import Dialog from '@/components/shared/Dialog';
import Button from '@/components/shared/Button';
import {
  getChatByParticipantRequest,
  sendMessageByParticipantRequest,
} from '@/api';
import Input from '@/components/shared/Input';
import { useSelector } from 'react-redux';
import { profileSelector } from '@/store/profile/profileSlices';
import clsx from 'clsx';
import convertToLocaleString from '@/utils/convertToLocaleString';
import './ChatDialog.scss';

const baseClass = 'ChatDialog';

interface ChatDialogProps {
  chatId?: number | null;
  onClose: () => void;
  onOpen?: () => void;
}

const ChatDialog: FC<ChatDialogProps> = ({ chatId, onOpen, onClose }) => {
  const [message, setMessage] = React.useState('');
  const profile = useSelector(profileSelector);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  // const inputRef = useRef<HTMLInputElement>(null);

  const {
    chat,
    chatIsFetching,
    chatIsLoading,
    chatRefetch,
    chatIsFetched,
    chatIsLoadingError,
    chatIsError,
    chatError,
    chatIsSuccess,
    isPending,
    sendMessage,
  } = useChats(chatId);

  const { mutate } = useReadChat();

  const sendMessageHandler = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (message && chatId) {
        sendMessage({ recipientId: chatId, message }); // Send message via mutation
        setMessage(''); // Clear the input
      }
    },
    [message, chatId, sendMessage],
  );

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      if (chat?.messages?.length) {
        const isUnread = chat.messages.some((message) => {
          return !message.isRead && message.sender.id !== profile?.id;
        });
        if (isUnread) {
          mutate({ chatId: chat.id });
        }
      }
    }
  }, [chat, profile, mutate]);

  useEffect(() => {
    if (!chatId) {
      setMessage('');
    }
  }, [chatId]);

  // useEffect(() => {
  //   console.log('chatId', chatId);
  //   if (chatId && inputRef.current) {
  //     inputRef.current.focus();
  //   }
  // }, [chatId]);

  const uniqueMessages = chat?.messages
    ? chat.messages.reduce((acc: Message[], message) => {
        if (!acc.find((m) => m.id === message.id)) {
          acc.push(message);
        }
        return acc;
      }, [])
    : [];

  return (
    <Dialog
      className={baseClass}
      isOpen={!!chatId}
      onOpen={onOpen}
      onClose={onClose}
      position={'right'}
      title={chat?.title}
      isLoader={chatIsLoading || chatIsFetching}
      footer={
        <form className={`${baseClass}--form`} onSubmit={sendMessageHandler}>
          {/*<Input*/}
          {/*  className={`${baseClass}--input`}*/}
          {/*  placeholder="Type your message"*/}
          {/*  value={message}*/}
          {/*  onChange={(e) => {*/}
          {/*    setMessage(e.target.value);*/}
          {/*  }}*/}
          {/*/>*/}
          <TextField
            // ref={inputRef}
            autoFocus
            className={`${baseClass}--input`}
            placeholder={'Message'}
            size={'small'}
            value={message}
            // multiline
            // maxRows={4}
            onChange={(e) => setMessage(e.target.value)}
          />
          <Button
            className={`${baseClass}--button`}
            // onClick={sendMessage}
            isDisabled={isPending || !message}
            type={'submit'}
          >
            Send
          </Button>
        </form>
      }
    >
      {chat && profile && (
        <div className={`${baseClass}--list `}>
          {uniqueMessages.map((message) => (
            <div
              key={message.id}
              className={clsx(`${baseClass}--list-item-wrap`, {
                [`${baseClass}--list-item-wrap-sender`]:
                  message.sender.id === profile.id,
                [`${baseClass}--list-item-wrap-unread`]: !message.isRead,
              })}
            >
              <div
                // className={`${baseClass}--list-item`}
                className={clsx(`${baseClass}--list-item`, {
                  [`${baseClass}--list-item-sender`]:
                    message.sender.id === profile.id,
                  // [`${baseClass}--list-item-unread`]: !message.isRead,
                })}
              >
                <span className={`${baseClass}--list-item-name`}>
                  {message.sender.firstname} {message.sender.lastname}
                </span>
                <span className={`${baseClass}--list-item-text`}>
                  {message.text}
                </span>
                <span className={`${baseClass}--list-item-date`}>
                  {convertToLocaleString(message.createdAt)}
                </span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}
    </Dialog>
  );
};

export default ChatDialog;
